import { Camera, Scene, Vector3 } from "three";
import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls";
import CurrentPlayer from "../Player/CurrentPlayer";
import PlayerPhysics from "../Player/PlayerPhysics";
import UserControls, { getYLockedRotation } from "./UserControls";
import { IMovement } from "./Movement";
import { JUMP_POWER, MOVE_SPEED } from "../../shared/constants";

class UserControlsKeyboard implements UserControls {
    public scene: Scene;

    public gameObject: CurrentPlayer;

    public physics: PlayerPhysics; // TODO does it need to be separate?

    public controls: PointerLockControls;

    public jumpPower: number;

    public moveSpeed: number;

    public movement: IMovement;

    public canJump: boolean;

    constructor(
        camera: Camera,
        scene: Scene,
        gameObject: CurrentPlayer,
        physics: PlayerPhysics,
        showMenu: () => void,
        hideMenu: () => void
    ) {
        this.scene = scene;
        this.gameObject = gameObject;
        this.physics = physics;
        this.controls = new PointerLockControls(camera);
        this.jumpPower = JUMP_POWER;
        this.moveSpeed = MOVE_SPEED;
        this.movement = {
            down: false,
            jump: false,
            left: false,
            right: false,
            up: false,
        };
        this.canJump = false;
        this.initialiseControls(showMenu, hideMenu);
    }

    public initialiseControls(
        showMenu: () => void,
        hideMenu: () => void
    ): void {
        const { scene, controls, movement, gameObject } = this;

        // TODO do I actually need this?
        controls
            .getObject()
            .position.set(
                gameObject.position.x,
                gameObject.position.y,
                gameObject.position.y
            );

        controls.addEventListener("lock", () => {
            hideMenu();
        });
        controls.addEventListener("unlock", () => {
            showMenu();
        });
        scene.add(controls.getObject());
        const onKeyDown = (event: KeyboardEvent) => {
            switch (event.keyCode) {
                case 38: // up
                case 87: // w
                    movement.up = true;
                    break;
                case 37: // left
                case 65: // a
                    movement.left = true;
                    break;
                case 40: // down
                case 83: // s
                    movement.down = true;
                    break;
                case 39: // right
                case 68: // d
                    movement.right = true;
                    break;
                case 32: // space
                    movement.jump = true;
                    break;
                case 9: // tab
                    gameObject.nextWeapon();
                    event.preventDefault();
                    break;
                default:
                    break;
            }
        };
        const onKeyUp = (event: KeyboardEvent) => {
            switch (event.keyCode) {
                case 38: // up
                case 87: // w
                    movement.up = false;
                    break;
                case 37: // left
                case 65: // a
                    movement.left = false;
                    break;
                case 40: // down
                case 83: // s
                    movement.down = false;
                    break;
                case 39: // right
                case 68: // d
                    movement.right = false;
                    break;
                case 32: // space
                    movement.jump = false;
                    break;
                default:
                    break;
            }
        };
        document.addEventListener("keydown", onKeyDown, false);
        document.addEventListener("keyup", onKeyUp, false);
    }

    public update(delta: number): void {
        const {
            controls,
            moveSpeed,
            movement,
            jumpPower,
            physics,
            gameObject,
        } = this;

        const camera = controls.getObject();

        camera.position.set(
            gameObject.position.x,
            gameObject.position.y,
            gameObject.position.z
        );

        const direction = new Vector3(
            Number(movement.right) - Number(movement.left),
            0,
            Number(movement.down) - Number(movement.up)
        );

        direction.applyQuaternion(camera.quaternion);
        direction.setY(0);
        direction.normalize();

        if (movement.up || movement.down || movement.left || movement.right) {
            this.gameObject.velocity.z += direction.z * delta * moveSpeed;
            this.gameObject.velocity.x += direction.x * delta * moveSpeed;
        }
        if (movement.jump && physics.onGround) {
            this.gameObject.velocity.y += jumpPower;
            physics.onGround = false;
        }

        // Update player rotation from controls, since the controls define the player not the other way around
        if (gameObject.model) {
            const gottenRotation = getYLockedRotation(gameObject.model, camera);
            gameObject.rotation.set(
                gottenRotation.x,
                gottenRotation.y,
                gottenRotation.z
            );
        }
    }

    public lock(): void {
        this.controls.lock();
    }
}

export default UserControlsKeyboard;
